/**
 * useBrokChat — thin SSE chat hook for the Brok AI agent.
 *
 * Public API: messages, streaming, loading, error, sendMessage, confirm, reject, rueckfrage.
 *
 * Exported pure helpers (testable without DOM):
 *   splitSseFrames — buffer splitting on \n\n boundaries (chunk-safe)
 *   parseSseEvent  — parse a single SSE frame to AgentSseEvent
 *   reduceEvent    — reducer: (messages, event) → BrokUiMessage[]
 *
 * P2b changes:
 *   - confirm(toolCallId)           → POST /agent/confirm {decision:'approve'}   (structured)
 *   - reject(toolCallId, note)      → POST /agent/confirm {decision:'reject', note}
 *   - rueckfrage(toolCallId, note)  → POST /agent/confirm {decision:'rueckfrage', note}
 *   - NL confirm is handled by the BACKEND (streamChat NL-branch) — no hook change needed.
 *   - consumeSse() is extracted from sendMessage so both paths reuse the same loop.
 *
 * [Source: Story P1b spec §7, Story P2b spec §3.3]
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AgentSseEvent, AgentMessageDto } from '@brokly/shared';
import { apiClient, agentChatStream, agentConfirmStream } from '../../../lib/api-client';
import type { AgentDecision } from '@brokly/shared';
import { qk } from '../../../lib/query-keys';
import { BROK_KICKOFF_PROMPT } from '../welcome';
import type { BrokUiMessage } from '../brok-chat.types';

// ── Pure helpers ──────────────────────────────────────────────────────────────

/**
 * Split raw SSE buffer on `\n\n` boundaries.
 * Returns complete frames + the trailing partial chunk to keep buffering.
 */
export function splitSseFrames(buffer: string): { frames: string[]; rest: string } {
  const parts = buffer.split('\n\n');
  // The last element is either empty (if buffer ended with \n\n) or a partial frame.
  const rest = parts.pop() ?? '';
  return { frames: parts.filter((f) => f.trim().length > 0), rest };
}

/**
 * Parse a single SSE frame (e.g. `data: {...}`) into an AgentSseEvent.
 * Returns null on parse failure.
 */
export function parseSseEvent(frame: string): AgentSseEvent | null {
  const line = frame
    .split('\n')
    .find((l) => l.startsWith('data:'));
  if (!line) return null;
  const json = line.slice('data:'.length).trim();
  try {
    return JSON.parse(json) as AgentSseEvent;
  } catch {
    return null;
  }
}

/**
 * Reducer: apply one AgentSseEvent to the current BrokUiMessage array.
 * Returns a new array (immutable update).
 */
export function reduceEvent(
  messages: BrokUiMessage[],
  event: AgentSseEvent,
): BrokUiMessage[] {
  switch (event.type) {
    case 'token': {
      // Find or create a streaming assistant message at the end
      const last = messages[messages.length - 1];
      if (last && last.kind === 'assistant' && last.id === '__streaming__') {
        // Append token to the existing streaming bubble
        return [
          ...messages.slice(0, -1),
          { ...last, text: last.text + event.delta },
        ];
      }
      // Start a new streaming assistant message
      return [
        ...messages,
        { kind: 'assistant', id: '__streaming__', text: event.delta },
      ];
    }

    case 'tool_call': {
      return [
        ...messages,
        {
          kind: 'tool',
          id: `tc-${event.toolCallId}`,
          toolCallId: event.toolCallId,
          toolName: event.toolName,
          args: event.args,
          status: 'running',
        },
      ];
    }

    case 'tool_result': {
      return messages.map((m) => {
        if (m.kind === 'tool' && m.toolCallId === event.toolCallId) {
          return { ...m, status: 'done' as const, result: event.result };
        }
        return m;
      });
    }

    case 'awaiting_confirmation': {
      return [
        ...messages,
        {
          kind: 'confirmation',
          id: `conf-${event.toolCallId}`,
          toolCallId: event.toolCallId,
          toolName: event.toolName,
          summary: event.summary,
          facts: event.facts,
          decision: 'pending',
        },
      ];
    }

    case 'confirmation_resolved': {
      // FIX A: reconcile the matching confirmation card to its final, server-authoritative
      // state on ALL paths (button AND typed "ja"). This guarantees a card can never keep
      // live buttons after resolution, and a FAILED action never stays shown as "Bestätigt.".
      return messages.map((m) => {
        if (m.kind === 'confirmation' && m.toolCallId === event.toolCallId) {
          return { ...m, decision: event.decision };
        }
        return m;
      });
    }

    case 'message_complete': {
      // Finalize the streaming assistant message by assigning its real id
      return messages.map((m) => {
        if (m.kind === 'assistant' && m.id === '__streaming__') {
          return { ...m, id: event.messageId };
        }
        return m;
      });
    }

    case 'error': {
      // Leave messages unchanged; error is surfaced via the error state
      return messages;
    }

    default:
      return messages;
  }
}

// ── Message DTO mapping ───────────────────────────────────────────────────────

function mapDtoToUiMessages(dtos: AgentMessageDto[]): BrokUiMessage[] {
  const result: BrokUiMessage[] = [];

  for (const dto of dtos) {
    if (dto.role === 'user') {
      // Feature B: rehydrate the attachment chip from the persisted metadata so it
      // survives a reload (the same shape the optimistic path sets on send).
      const attachment = dto.attachment
        ? { filename: dto.attachment.filename, attachmentId: dto.attachment.attachmentId ?? '' }
        : undefined;
      result.push({
        kind: 'user',
        id: dto.id,
        text: dto.content ?? '',
        ...(attachment ? { attachment } : {}),
      });
    } else if (dto.role === 'assistant') {
      if (dto.content) {
        result.push({
          kind: 'assistant',
          id: dto.id,
          text: dto.content,
        });
      }
      // assistant rows with only toolName/toolCallId (no content) are pure tool-call rows;
      // they will be matched by the corresponding tool result row below, so skip them here.
    } else if (dto.role === 'tool') {
      result.push({
        kind: 'tool',
        id: dto.id,
        toolCallId: dto.toolCallId ?? dto.id,
        toolName: dto.toolName ?? 'unknown',
        status: 'done',
        result: dto.toolResult ?? undefined,
      });
    }
  }

  return result;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/** The api's sentence of a refused request (`{ message }` in the JSON body), else «HTTP <status>». */
async function refusalMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: unknown };
    if (typeof body?.message === 'string' && body.message.trim() !== '') return body.message;
  } catch {
    // not JSON
  }
  return `HTTP ${response.status}`;
}

/**
 * Options for useBrokChat.
 * - `kickoff` (default true): auto-fire the hidden kickoff turn on an empty thread
 *   so Brok speaks first. The Heute Briefing sets this false — its static greeting
 *   + tiles ARE "Brok speaks first"; the chat below is for follow-ups only.
 *   [Source: docs/stories/ui-u10-brok-briefing-ray.md AC3]
 */
export interface UseBrokChatOptions {
  kickoff?: boolean;
}

export function useBrokChat(options?: UseBrokChatOptions) {
  const kickoffEnabled = options?.kickoff !== false;
  const queryClient = useQueryClient();

  // Server-state: the persisted thread (replayed on mount)
  const { data, isLoading } = useQuery({
    queryKey: qk.agentConversation(),
    queryFn: () => apiClient.agent.getConversation(),
    staleTime: Infinity, // we invalidate manually after each turn
    retry: false,
  });

  // UI state
  const [uiMessages, setUiMessages] = useState<BrokUiMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `ready` flips true once the persisted thread has hydrated — the Briefing
  // snapshots its baseline message count at this instant (baseline-slice delta).
  const [ready, setReady] = useState(false);

  // Populate messages from the fetched conversation once the query settles.
  // On success we hydrate the persisted thread; on error (data undefined, retry
  // disabled) we STILL flip `ready` so consumers that snapshot a baseline off it
  // (the Heute Briefing's delta-slice) degrade gracefully — otherwise the briefing
  // composer would silently drop every message the user sends. BrokScreen renders
  // the full `messages` array regardless, so this only affects baseline consumers.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!isLoading && !hydratedRef.current) {
      hydratedRef.current = true;
      if (data) setUiMessages(mapDtoToUiMessages(data.messages));
      setReady(true);
    }
  }, [isLoading, data]);

  // Abort controller for in-flight streams
  const abortRef = useRef<AbortController | null>(null);

  // Guard: kickoff sent exactly once on an empty thread
  const kickoffSentRef = useRef(false);

  /**
   * consumeSse — shared SSE reader loop.
   * Reads from the response body, splits frames, reduces events into UI state.
   * Used by both sendMessage (chat) and the confirm/reject/rueckfrage handlers.
   */
  const consumeSse = useCallback(
    async (response: Response): Promise<void> => {
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const { frames, rest } = splitSseFrames(buffer);
        buffer = rest;

        for (const frame of frames) {
          const event = parseSseEvent(frame);
          if (!event) continue;

          if (event.type === 'error') {
            setError(event.message);
            setStreaming(false);
            return;
          }

          if (event.type === 'message_complete') {
            setUiMessages((prev) => reduceEvent(prev, event));
            setStreaming(false);
            // Invalidate so reload replays from the persisted source of truth
            void queryClient.invalidateQueries({ queryKey: qk.agentConversation() });
            return;
          }

          setUiMessages((prev) => reduceEvent(prev, event));
        }
      }
    },
    [queryClient],
  );

  const sendMessage = useCallback(
    async (
      text: string,
      opts?: { hidden?: boolean; attachmentId?: string; attachmentName?: string },
    ) => {
      // Abort any existing stream
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setError(null);

      // Optimistically append user message (unless hidden). When a document was
      // attached this turn, carry the filename onto the message so the transcript
      // shows the "angehängt: <filename>" chip (Feature B — live/optimistic only).
      if (!opts?.hidden) {
        const attachment =
          opts?.attachmentId && opts?.attachmentName
            ? { filename: opts.attachmentName, attachmentId: opts.attachmentId }
            : undefined;
        setUiMessages((prev) => [
          ...prev,
          { kind: 'user', id: `user-opt-${Date.now()}`, text, ...(attachment ? { attachment } : {}) },
        ]);
      }

      setStreaming(true);

      try {
        const response = await agentChatStream(
          text,
          controller.signal,
          opts?.attachmentId,
          opts?.attachmentName,
        );
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        await consumeSse(response);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Intentional abort (e.g. unmount) — not an error
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        setStreaming(false);
      }
    },
    [consumeSse],
  );

  /**
   * newChat, start a fresh chat ("Neuer Chat", FIX 2).
   * POSTs the reset endpoint (server advances the context watermark + clears the active
   * attachment), then clears the local transcript and resets the hydration/kickoff guards
   * so a fresh kickoff can fire on the now-empty thread. After reset the previous PDF/thread
   * does NOT bleed in.
   */
  const newChat = useCallback(async () => {
    // Abort any in-flight stream first.
    abortRef.current?.abort();
    setError(null);
    try {
      await apiClient.agent.resetConversation();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return;
    }
    // Clear local UI + reset the hydration/kickoff guards so the re-fetched (empty) thread
    // re-hydrates and a fresh kickoff can fire.
    setUiMessages([]);
    setStreaming(false);
    hydratedRef.current = false;
    kickoffSentRef.current = false;
    setReady(false);
    await queryClient.invalidateQueries({ queryKey: qk.agentConversation() });
  }, [queryClient]);

  // Auto-fire kickoff on empty thread (once, after hydration).
  // Suppressed when kickoff:false (the Heute Briefing) — no hidden turn is fired.
  useEffect(() => {
    if (!isLoading && data && !kickoffSentRef.current) {
      kickoffSentRef.current = true;
      if (kickoffEnabled && data.messages.length === 0) {
        void sendMessage(BROK_KICKOFF_PROMPT, { hidden: true });
      }
      // else: thread already has messages, or kickoff disabled — never fire it.
    }
  }, [isLoading, data, sendMessage, kickoffEnabled]);

  // Abort in-flight stream on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  /**
   * confirm — approve the pending write action via the structured confirm endpoint.
   * Deterministic path: does NOT call sendMessage (no NL round-trip).
   * [Source: Story P2b spec §3.3]
   */
  const confirm = useCallback(
    async (toolCallId: string) => {
      // Optimistic: flip card to approved state
      setUiMessages((prev) =>
        prev.map((m) =>
          m.kind === 'confirmation' && m.toolCallId === toolCallId
            ? { ...m, decision: 'approved' as const }
            : m,
        ),
      );
      setStreaming(true);
      setError(null);
      try {
        const response = await agentConfirmStream(toolCallId, 'approve' as AgentDecision);
        if (!response.ok) {
          // Refused before the decision ran (e.g. 409 customer_read_only on an inaktiv customer): the card must
          // not stay «Bestätigt.», and the api's sentence is shown instead of the bare status.
          setUiMessages((prev) =>
            prev.map((m) =>
              m.kind === 'confirmation' && m.toolCallId === toolCallId ? { ...m, decision: 'failed' as const } : m,
            ),
          );
          throw new Error(await refusalMessage(response));
        }
        await consumeSse(response);
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        setStreaming(false);
      }
    },
    [consumeSse],
  );

  /**
   * reject — reject the pending write action via the structured confirm endpoint.
   * note must be ≥10 chars (enforced server-side; the card's submit button is disabled
   * until that threshold is met, preventing a wasted round-trip).
   * [Source: Story P2b spec §3.3]
   */
  const reject = useCallback(
    async (toolCallId: string, note: string) => {
      setUiMessages((prev) =>
        prev.map((m) =>
          m.kind === 'confirmation' && m.toolCallId === toolCallId
            ? { ...m, decision: 'rejected' as const }
            : m,
        ),
      );
      setStreaming(true);
      setError(null);
      try {
        const response = await agentConfirmStream(toolCallId, 'reject' as AgentDecision, note);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        await consumeSse(response);
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        setStreaming(false);
      }
    },
    [consumeSse],
  );

  /**
   * rueckfrage — send a follow-up question for the pending write action.
   * note must be ≥10 chars (enforced server-side + card UI).
   * [Source: Story P2b spec §3.3]
   */
  const rueckfrage = useCallback(
    async (toolCallId: string, note: string) => {
      setUiMessages((prev) =>
        prev.map((m) =>
          m.kind === 'confirmation' && m.toolCallId === toolCallId
            ? { ...m, decision: 'rueckfrage' as const }
            : m,
        ),
      );
      setStreaming(true);
      setError(null);
      try {
        const response = await agentConfirmStream(toolCallId, 'rueckfrage' as AgentDecision, note);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        await consumeSse(response);
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        setStreaming(false);
      }
    },
    [consumeSse],
  );

  return {
    messages: uiMessages,
    streaming,
    loading: isLoading,
    ready,
    error,
    sendMessage,
    newChat,
    confirm,
    reject,
    rueckfrage,
  };
}
